import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE, getUserErrorMessage, parseJsonResponse } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import NumericInput from '../NumericInput';

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function InventoryStatCard({ label, value, hint, gold, danger }) {
  return (
    <div className={`sp-inv-stat-card ${danger ? 'sp-inv-stat-card--danger' : ''}`}>
      <span className="sp-stat-label">{label}</span>
      <strong className={`sp-stat-value ${gold ? 'sp-stat-gold' : ''}`}>{value}</strong>
      {hint ? <span className="sp-stat-hint">{hint}</span> : null}
    </div>
  );
}

function getStockStatus(stock) {
  if (stock <= 0) return { label: 'Out of stock', className: 'sp-inv-badge--out' };
  if (stock <= 5) return { label: 'Low stock', className: 'sp-inv-badge--low' };
  return { label: 'In stock', className: 'sp-inv-badge--in' };
}

function ProductInventoryCard({ product, shopAccountId, onUpdated }) {
  const [stockInput, setStockInput] = useState(
    product.stock === 0 || product.stock ? String(product.stock) : ''
  );
  const [priceInput, setPriceInput] = useState(product.price ? String(product.price) : '');
  const [discountInput, setDiscountInput] = useState(
    product.discount ? String(product.discount) : ''
  );
  const [saving, setSaving] = useState(false);

  const status = getStockStatus(Number(product.stock || 0));

  const saveStock = async () => {
    setSaving(true);
    try {
      const response = await authFetch(
        `${API_BASE}/v1/shop/accounts/${shopAccountId}/products/${product.id}/stock`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stock: Number(stockInput) }),
        }
      );
      const result = await parseJsonResponse(response);
      onUpdated(result.data);
      setStockInput(String(result.data.stock));
      toast.success(`Stock updated for ${product.name}`);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to update stock.'));
    } finally {
      setSaving(false);
    }
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      const response = await authFetch(
        `${API_BASE}/v1/shop/accounts/${shopAccountId}/products/${product.id}/pricing`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            price: Number(priceInput),
            discount: Number(discountInput || 0),
          }),
        }
      );
      const result = await parseJsonResponse(response);
      onUpdated(result.data);
      setPriceInput(String(result.data.price));
      setDiscountInput(result.data.discount ? String(result.data.discount) : '');
      toast.success(`Pricing updated for ${product.name}`);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to update pricing.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sp-inv-product-card">
      <div className="sp-inv-product-head">
        <div>
          <h4 className="sp-inv-product-name">{product.name}</h4>
          <p className="sp-inv-product-meta">
            Size {product.capacity} ml
            {Number(product.price) > 0 ? (
              <>
                {' · '}
                {formatMoney(product.price)}
                {Number(product.discount) > 0 ? ` · Disc ${formatMoney(product.discount)}` : ''}
              </>
            ) : (
              <span className="sp-inv-pricing-pending"> · Price not set</span>
            )}
          </p>
        </div>
        <span className={`sp-inv-badge ${status.className}`}>{status.label}</span>
      </div>

      <div className="sp-inv-product-stock">
        <div className="sp-inv-stock-display">
          <span className="sp-inv-stock-label">Pieces in stock</span>
          <strong className="sp-inv-stock-value">{product.stock ?? 0}</strong>
          <span className="sp-inv-stock-value-hint">{formatMoney(product.stockValue || 0)} value</span>
        </div>
        <code className="sp-inv-barcode">{product.barcode}</code>
      </div>

      {product.barcodeImage && (
        <img src={product.barcodeImage} alt="" className="sp-inv-barcode-img" />
      )}

      <div className="sp-inv-pricing-block">
        <span className="sp-inv-section-label">Pricing (super admin)</span>
        <div className="sp-inv-stock-form">
          <div className="sp-inv-stock-field">
            <label htmlFor={`price-${product.id}`}>Price (Rs)</label>
            <NumericInput
              id={`price-${product.id}`}
              min="1"
              step="1"
              className="sp-form-input"
              placeholder="Price"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
            />
          </div>
          <div className="sp-inv-stock-field">
            <label htmlFor={`discount-${product.id}`}>Discount (Rs)</label>
            <NumericInput
              id={`discount-${product.id}`}
              min="0"
              step="1"
              className="sp-form-input"
              placeholder="Discount"
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="sp-inv-action-btn"
            disabled={saving || !priceInput}
            onClick={savePricing}
          >
            Save
          </button>
        </div>
      </div>

      <div className="sp-inv-stock-form">
        <div className="sp-inv-stock-field">
          <label htmlFor={`stock-set-${product.id}`}>Stock</label>
          <NumericInput
            id={`stock-set-${product.id}`}
            min="0"
            className="sp-form-input"
            placeholder="Stock"
            value={stockInput}
            onChange={(e) => setStockInput(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="sp-inv-action-btn"
          disabled={saving || stockInput === ''}
          onClick={saveStock}
        >
          Update
        </button>
      </div>
    </div>
  );
}

export default function ShopInventoryPanel({
  shopAccountId,
  inventory,
  inventoryStats,
  onInventoryChange,
}) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const stats = inventoryStats || {
    totalProducts: inventory.length,
    inStock: 0,
    outOfStock: 0,
    lowStock: 0,
    totalUnits: 0,
    stockValue: 0,
  };

  const filteredProducts = useMemo(() => {
    return inventory.filter((product) => {
      const stock = Number(product.stock || 0);
      const term = search.trim().toLowerCase();

      if (filter === 'in' && stock <= 0) return false;
      if (filter === 'out' && stock > 0) return false;
      if (filter === 'low' && (stock <= 0 || stock > 5)) return false;

      if (!term) return true;
      return (
        product.name.toLowerCase().includes(term) ||
        product.barcode.toLowerCase().includes(term)
      );
    });
  }, [inventory, filter, search]);

  const handleProductUpdated = (updatedProduct) => {
    const nextInventory = inventory.map((item) =>
      item.id === updatedProduct.id
        ? {
            ...item,
            ...updatedProduct,
            stockValue:
              Number(updatedProduct.stock ?? item.stock ?? 0) *
              Number(updatedProduct.price ?? item.price ?? 0),
          }
        : item
    );
    onInventoryChange(nextInventory);
  };

  return (
    <div className="sp-inventory-panel">
      <div className="sp-detail-head">
        <div>
          <h3 className="sp-section-title sp-section-title--flush">Shop Inventory</h3>
          <p className="sp-section-desc">
            Products are created in shop panel. Super admin sets price, discount, and stock.
          </p>
        </div>
      </div>

      <div className="sp-stats-row sp-stats-row--3 sp-inv-stats-row">
        <InventoryStatCard label="Total Products" value={stats.totalProducts} hint="Created in shop" />
        <InventoryStatCard
          label="Total Units"
          value={stats.totalUnits}
          hint="Pieces across all products"
          gold
        />
        <InventoryStatCard
          label="Stock Value"
          value={formatMoney(stats.stockValue)}
          hint="Qty × listed price"
          gold
        />
      </div>

      <div className="sp-stats-row sp-stats-row--3 sp-inv-stats-row">
        <InventoryStatCard label="In Stock" value={stats.inStock} hint="Available to sell" />
        <InventoryStatCard label="Low Stock" value={stats.lowStock} hint="5 pieces or less" danger />
        <InventoryStatCard label="Out of Stock" value={stats.outOfStock} hint="Cannot be sold" danger />
      </div>

      <div className="sp-inv-toolbar">
        <div className="sp-search sp-inv-search">
          <span aria-hidden="true">⌕</span>
          <input
            type="text"
            placeholder="Search product or barcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="sp-filter-row sp-filter-row--flush">
          {[
            { id: 'all', label: 'All' },
            { id: 'in', label: 'In Stock' },
            { id: 'low', label: 'Low Stock' },
            { id: 'out', label: 'Out of Stock' },
          ].map((chip) => (
            <button
              key={chip.id}
              type="button"
              className={`sp-filter-chip ${filter === chip.id ? 'sp-filter-chip--active' : ''}`}
              onClick={() => setFilter(chip.id)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {filteredProducts.length ? (
        <div className="sp-inv-grid">
          {filteredProducts.map((product) => (
            <ProductInventoryCard
              key={product.id}
              product={product}
              shopAccountId={shopAccountId}
              onUpdated={handleProductUpdated}
            />
          ))}
        </div>
      ) : (
        <p className="sp-muted sp-muted--compact">No products match this filter.</p>
      )}
    </div>
  );
}
